import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Columns2, Package, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRecommendationOptions } from '@/hooks/use-product-recommendations';
import { cn } from '@/lib/utils';
import type { RecommendationClient, RecommendationOption, SelectionKind } from '@/types';
import type { DiscountExclusions } from '../../settings/types';
import { PILL_BUTTON } from '../../ui-chrome';
import { EXCLUSION_KINDS } from '../exclusion-kinds';

/** The search box waits for a pause in typing before asking the catalogue. */
const SEARCH_DELAY_MS = 300;

const useSettledValue = (value: string): string => {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setSettled(value), SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [value]);
	return settled;
};

const EXCLUSIONS_HELP = 'If products are chosen here, the agent will not provide any discounts on them.';

// The shared tabs drawn as this drawer's underlined strip: no track, no pill, a 2px rule under the active one.
const TAB_LIST = 'h-auto w-full shrink-0 justify-start gap-4 overflow-visible rounded-none border-0 border-b border-gray-500 bg-transparent p-0 px-4';
const TAB_TRIGGER =
	'h-10 flex-none rounded-none border-0 border-b-2 border-transparent px-0 py-[11px] text-[13px] leading-[18px] font-normal text-gray-800 data-[state=active]:border-ink-strong data-[state=active]:font-medium data-[state=active]:text-ink-strong data-[state=active]:drop-shadow-none';

const isSelectionKind = (value: string): value is SelectionKind => EXCLUSION_KINDS.some((item) => item.key === value);

interface DiscountExclusionsSheetProps {
	/** The kind the drawer opens on; null = closed. */
	openKind: SelectionKind | null;
	exclusions: DiscountExclusions;
	client: RecommendationClient;
	scope: string;
	onApply: (exclusions: DiscountExclusions) => void;
	onClose: () => void;
}

/**
 * The discount Exclusions drawer (Figma Kim-AI 6452:61747): pick the products, tags and
 * collections the agent never discounts. Ticks stay local until Exclude hands them to the tab's
 * draft, so Cancel or closing the drawer drops them; the tab's Save sends them.
 */
export const DiscountExclusionsSheet = ({
	openKind,
	exclusions,
	client,
	scope,
	onApply,
	onClose
}: DiscountExclusionsSheetProps) => {
	const [expanded, setExpanded] = useState(false);
	return (
		<Sheet open={openKind !== null} onOpenChange={(open) => !open && onClose()}>
			<SheetContent
				side='right'
				showClose={false}
				className={cn(
					'w-full gap-0 border-gray-300 bg-white/80 p-0 shadow-[-3px_0_12px_0_rgba(0,0,0,0.08)] backdrop-blur-[6px] sm:max-w-none',
					expanded ? 'sm:w-[calc(100vw-48px)]' : 'sm:w-[520px]'
				)}
			>
				{openKind && (
					// Keyed so each opening starts from the saved exclusions, not the last abandoned ticks.
					<ExclusionsPicker
						key={openKind}
						initialKind={openKind}
						exclusions={exclusions}
						client={client}
						scope={scope}
						onToggleExpanded={() => setExpanded((current) => !current)}
						onApply={onApply}
						onClose={onClose}
					/>
				)}
			</SheetContent>
		</Sheet>
	);
};

interface ExclusionsPickerProps {
	initialKind: SelectionKind;
	exclusions: DiscountExclusions;
	client: RecommendationClient;
	scope: string;
	onToggleExpanded: () => void;
	onApply: (exclusions: DiscountExclusions) => void;
	onClose: () => void;
}

const ExclusionsPicker = ({
	initialKind,
	exclusions,
	client,
	scope,
	onToggleExpanded,
	onApply,
	onClose
}: ExclusionsPickerProps) => {
	const [kind, setKind] = useState<SelectionKind>(initialKind);
	const [picked, setPicked] = useState<DiscountExclusions>(exclusions);
	const [query, setQuery] = useState('');
	const search = useSettledValue(query.trim());
	const rows = useRecommendationOptions(client, scope, kind, search, true);
	const current = EXCLUSION_KINDS.find((item) => item.key === kind) ?? EXCLUSION_KINDS[0];

	const toggle = (id: string, checked: boolean) =>
		setPicked((value) => ({
			...value,
			[kind]: checked ? [...value[kind], id] : value[kind].filter((item) => item !== id)
		}));

	return (
		<div className='flex h-full min-h-0 flex-col'>
			<header className='flex shrink-0 flex-col'>
				<div className='flex items-center justify-between gap-3 px-4 pt-3 pb-3'>
					<div className='flex min-w-0 flex-col'>
						<SheetTitle className='text-sm leading-5 font-semibold text-ink'>Exclusions</SheetTitle>
						<SheetDescription className='text-[11px] leading-4 text-gray-800'>{EXCLUSIONS_HELP}</SheetDescription>
					</div>
					<Button
						type='button'
						variant='ghost'
						size='icon'
						aria-label='Expand the exclusions panel'
						onClick={onToggleExpanded}
						className='size-7 shrink-0 rounded-full text-ink-strong'
					>
						<Columns2 className='size-3.5' strokeWidth={1.4} />
					</Button>
				</div>
			</header>
			<Tabs
				value={kind}
				onValueChange={(value) => {
					if (!isSelectionKind(value)) return;
					setKind(value);
					setQuery('');
				}}
				className='flex min-h-0 flex-1 flex-col gap-0'
			>
				<TabsList aria-label='Exclusion type' className={TAB_LIST}>
					{EXCLUSION_KINDS.map((item) => (
						<TabsTrigger key={item.key} value={item.key} className={TAB_TRIGGER}>
							{item.label}
						</TabsTrigger>
					))}
				</TabsList>
				{/* Only the selected kind's panel mounts, so one catalogue search runs at a time. */}
				{EXCLUSION_KINDS.map((item) => (
					<TabsContent key={item.key} value={item.key} className='flex min-h-0 flex-1 flex-col gap-3 px-2 pt-4'>
						<label className='flex h-8 shrink-0 items-center gap-2.5 rounded-full border border-gray-500 bg-white px-2.5'>
							<Search className='size-3.5 text-gray-800' strokeWidth={1.5} />
							<input
								value={query}
								maxLength={200}
								onChange={(event) => setQuery(event.target.value)}
								placeholder={current.search}
								aria-label={current.search}
								className='min-w-0 flex-1 bg-transparent text-[11px] leading-4 text-ink outline-none placeholder:text-gray-800'
							/>
						</label>
						<div className='min-h-0 flex-1 overflow-y-auto'>
							{rows.error && (
								<p role='alert' className='p-2 text-xs text-red-600'>
									{rows.error}
									<Button variant='ghost' onClick={rows.retry}>
										Retry
									</Button>
								</p>
							)}
							{rows.loading && <p className='p-2 text-xs text-gray-800'>Loading {current.label.toLowerCase()}…</p>}
							{!rows.loading && !rows.error && !rows.options.length && (
								<p className='p-2 text-xs text-gray-800'>No {current.label.toLowerCase()} found.</p>
							)}
							{rows.options.map((option) => (
								<ExclusionRow
									key={option.id}
									option={option}
									kind={kind}
									checked={picked[kind].includes(option.id)}
									onCheckedChange={(checked) => toggle(option.id, checked)}
									client={client}
									scope={scope}
								/>
							))}
							{rows.nextCursor && (
								<Button variant='ghost' disabled={rows.busy} onClick={() => void rows.loadMore()}>
									{rows.busy ? 'Loading…' : 'Load more'}
								</Button>
							)}
						</div>
					</TabsContent>
				))}
			</Tabs>

			<footer className='flex h-[60px] shrink-0 items-center justify-between border-t border-gray-400 p-4'>
				<span className='text-xs leading-4 font-medium text-indigo-500'>{picked[kind].length} Selected</span>
				<div className='flex items-center gap-1.5'>
					<Button
						type='button'
						variant='outline'
						onClick={onClose}
						className='h-[30px] rounded-[20px] border-gray-300 bg-white px-3 text-xs font-medium text-ink shadow-none'
					>
						Cancel
					</Button>
					<Button
						type='button'
						onClick={() => onApply(picked)}
						className={cn(PILL_BUTTON, 'h-[30px] border border-text-quote bg-ink px-3.5 text-xs text-gray-50')}
					>
						Exclude
					</Button>
				</div>
			</footer>
		</div>
	);
};

interface ExclusionRowProps {
	option: RecommendationOption;
	kind: SelectionKind;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	client: RecommendationClient;
	scope: string;
}

/** One catalogue row; a tag or collection opens to show the products it covers. */
const ExclusionRow = ({ option, kind, checked, onCheckedChange, client, scope }: ExclusionRowProps) => {
	const [open, setOpen] = useState(false);
	const isGroup = kind !== 'productIds';
	return (
		<div className={cn('flex flex-col border-b border-gray-200 last:border-b-0', open && 'rounded-2xl border-b-0 bg-gray-300')}>
			<div className='flex h-11 items-center gap-2.5 px-1'>
				<span className='flex size-6 shrink-0 items-center justify-center'>
					<Checkbox
						className='size-3.5 rounded'
						aria-label={`Exclude ${option.label}`}
						checked={checked}
						onCheckedChange={(value) => onCheckedChange(value === true)}
					/>
				</span>
				{kind === 'productIds' && <ProductThumb imageUrl={option.imageUrl} />}
				<span className='min-w-0 flex-1 truncate text-sm leading-5 text-ink'>{option.label}</span>
				{isGroup && (
					<Button
						type='button'
						variant='ghost'
						size='icon'
						aria-label={`${open ? 'Hide' : 'Show'} products in ${option.label}`}
						aria-expanded={open}
						onClick={() => setOpen((value) => !value)}
						className='size-7 shrink-0 rounded-full text-gray-800'
					>
						{open ? <ChevronDown className='size-3.5' strokeWidth={1.4} /> : <ChevronRight className='size-3.5' strokeWidth={1.4} />}
					</Button>
				)}
			</div>
			{isGroup && open && (
				<GroupProducts client={client} scope={scope} group={{ kind: kind === 'tags' ? 'tags' : 'collectionIds', id: option.id }} />
			)}
		</div>
	);
};

const ProductThumb = ({ imageUrl }: { imageUrl?: string }) =>
	imageUrl ? (
		<img src={imageUrl} alt='' className='size-6 shrink-0 rounded-md object-cover' />
	) : (
		<span className='flex size-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-800'>
			<Package className='size-3.5' />
		</span>
	);

/** The products under an opened tag or collection, on a guide line (read only). */
const GroupProducts = ({
	client,
	scope,
	group
}: {
	client: RecommendationClient;
	scope: string;
	group: { kind: 'tags' | 'collectionIds'; id: string };
}) => {
	const rows = useRecommendationOptions(client, scope, 'productIds', '', true, group);
	return (
		<div className='flex gap-2 pr-3 pb-1 pl-[15px]'>
			<span aria-hidden className='w-3 shrink-0 border-l border-gray-500' />
			<div className='flex min-w-0 flex-1 flex-col'>
				{rows.loading && <p className='p-1.5 text-xs text-gray-800'>Loading products…</p>}
				{rows.error && (
					<p role='alert' className='p-1.5 text-xs text-red-600'>
						{rows.error}
					</p>
				)}
				{!rows.loading && !rows.error && !rows.options.length && (
					<p className='p-1.5 text-xs text-gray-800'>No products in this group.</p>
				)}
				{rows.options.map((row) => (
					<div key={row.id} className='flex items-center gap-2.5 p-1.5'>
						<ProductThumb imageUrl={row.imageUrl} />
						<span className='min-w-0 flex-1 truncate text-[13px] leading-[18px] text-ink'>{row.label}</span>
					</div>
				))}
				{rows.nextCursor && (
					<Button variant='ghost' disabled={rows.busy} onClick={() => void rows.loadMore()}>
						Load more products
					</Button>
				)}
			</div>
		</div>
	);
};
